import { useState, useEffect, useRef, useCallback } from "react";
import { updatePostHogIdentity, useAuth } from "../context/AuthContext";
import { Post } from "../types";
import { motion } from "framer-motion";
import { Menu, ArrowUpDown, X, Upload, Linkedin, ChevronDown, Database, Gift, Sparkles, Cpu, CheckSquare } from "lucide-react";
import { Loader } from '@/components/ui/loader';
import Lottie from "lottie-react";
import successAnimation from "../animation/success.json";
import attentionAnimation from "../animation/Attention.json";
import nodataAnimation from "../animation/nodata.json";
import { Button } from "@/components/ui/button";
import { SearchBar } from "@/components/SearchBar";
import ReelGrid from "@/components/ReelGrid";
import { Sidebar } from "@/components/Sidebar";
import { Footer } from "@/components/Footer";
import { NewSidebarBadge } from "@/components/NewSidebarBadge";
import { useNavigate } from "react-router-dom";
import SubscriptionModal from "@/components/SubscriptionModal";
import PhoneNumberModal from "@/components/PhoneNumberModal";
import { SyncStatus } from "@/components/SyncStatus";
import { UnprocessedPostsNotification } from "@/components/UnprocessedPostsNotification";
import FilterPopup, { FilterState } from "@/components/FilterPopup";
import sampleReels from '../lib/sample_reels.json'; // Import the sample reels
import {ChromeIcon} from '../components/ui/ChromeIcon.tsx'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import axios from 'axios';
import { fetchPosts, fetchUserProfile, fetchSyncStatus, SyncStatusResponse, fetchCategories, fetchAllCategories, fetchPostCounts, fetchPlatformCounts, fetchSubscription, exportPosts, fetchPostDumpsStatus, processPostDumps, fetchAiCategorisationEligibility, fetchAiCategorisationStatus, startAiCategorisation, bulkDeletePosts, bulkArchivePosts, bulkMarkFavorite, type AiCategorisationEligibilityResponse, type Category, type PostCounts, type PlatformCounts, type Subscription } from '@/services/api';
import { initPostHog, captureEvent } from '@/utils/posthog';
import toast from 'react-hot-toast';
import { getPlatformApiValue, buildCategoryFilterParams } from '@/lib/utils';

const REWIND_END_DATE = new Date("2026-01-15T23:59:59");
const AI_CATEGORISATION_POLL_INTERVAL = 10000;

const HOME_PREFS_SORT_ORDER_KEY = 'instavault-home-sortOrder';
const HOME_PREFS_VIEW_MODE_KEY = 'instavault-home-viewMode';

// Export error codes from API (error.response?.data?.detail?.code)
const EXPORT_ERROR = {
  EXCEL_EXPORT_DISABLED: {
    message: "Your plan doesn't support excel export. Please upgrade to PRO or EXPORT plan.",
    primaryLabel: "Upgrade Now",
    primaryAction: "subscription" as const,
    secondaryLabel: "Cancel",
  },
  EXCEL_EXPORT_LIMIT_REACHED: {
    message: "You have exhausted your excel export requests limit. Please renew your plan or reach out to us at hello@instavault.co if you have any questions",
    primaryLabel: "Renew Now",
    primaryAction: "subscription" as const,
    secondaryLabel: "Cancel",
  },
  NO_POSTS_TO_EXPORT: {
    message: "You have not synced any posts yet. Please sync your posts to continue export.",
    primaryLabel: "Got it",
    primaryAction: "close" as const,
    secondaryLabel: "Cancel",
  },
} as const;

const EXPORT_ERROR_DEFAULT = {
  message: "We are unable to process your export request at this moment. Please try again or reach out to us at hello@instavault.co if the error persists.",
  primaryLabel: "Try Again",
  primaryAction: "retry" as const,
  secondaryLabel: "Cancel",
};

interface CategorySelection {
  id: string;
  category_enum: string;
  category_display_value: string;
  is_folder: boolean;
}
import { isInstavaultExtensionLoaded, syncChromeBookmarks } from '@/lib/extensionUtils';
import {
  trackDashboardViewed,
  trackSearchExecuted,
  trackOnboardingPhonePrompt,
  trackArchivedPostsViewed,
  trackPlatformFilterApplied,
  trackCategoryFilterApplied,
  trackSortOrderChanged,
  trackFavouritePostsViewed
} from '@/utils/eventTracking';
import { sessionTracker } from '@/utils/sessionTracker';
import { TikTokIcon } from "@/components/TikTokIcon";
import { RedditIcon } from "@/components/RedditIcon";
import { CategoryChangePopup } from "@/components/CategoryChangePopup";
import { BulkActionToolbar } from "@/components/BulkActionToolbar";


const PAGE_SIZE = 9;

export const HomePage = () => {
  const navigate = useNavigate();
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const { token } = useAuth();
  const [syncStatus, setSyncStatus] = useState<SyncStatusResponse | null>(null);
  const previousSyncStatusRef = useRef<string | null>(null);
  const fetchReelsRef = useRef<((reset?: boolean, searchTerm?: string, forceArchived?: boolean, forceFavorites?: boolean) => Promise<void>) | null>(null);
  // Synchronous guard against re-entrant fetchReels calls — `loading` state updates are
  // async/batched, so two pagination triggers firing in the same tick can both read
  // `loading === false` and both start a fetch. This ref flips instantly, closing that gap.
  const isFetchingReelsRef = useRef(false);
  const [isAiCategorisationEligibilityLoading, setIsAiCategorisationEligibilityLoading] = useState(false);

  useEffect(() => {
    const checkNewUser = async () => {
      // First check localStorage
      const mobileNumber = localStorage.getItem('mobile');
      if (!mobileNumber) {
        // Check if subscription is expired
        const premiumUntil = localStorage.getItem('premiumUntil');
        if (premiumUntil && premiumUntil !== 'null' && premiumUntil !== 'undefined') {
          const expiryDate = new Date(premiumUntil);
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          // If subscription is expired, don't show phone modal
          if (expiryDate < today) {
            return;
          }
        }

        // Check if we've shown the modal today
        const lastShownDate = localStorage.getItem('phoneModalLastShown');
        const today = new Date().toDateString();

        if (lastShownDate !== today) {
          try {
            const profile = await fetchUserProfile();
            if (!profile.mobile) {
              // Track phone prompt shown
              trackOnboardingPhonePrompt();

              setShowPhoneModal(true);
              // Mark that we've shown the modal today
              localStorage.setItem('phoneModalLastShown', today);
            } else {
              // Store in localStorage for future checks
              localStorage.setItem('mobile', profile.mobile);
            }
          } catch (error) {
            console.error('Failed to fetch user profile:', error);
          }
        }
      }
    };

    if (token) {
      checkNewUser();
    }
  }, [token]);

  const checkSyncStatus = useCallback(async () => {
    try {
      const status = await fetchSyncStatus();
      const currentStatus = status?.sync_status || null;
      const previousStatus = previousSyncStatusRef.current;
      setSyncStatus(status);
      previousSyncStatusRef.current = currentStatus;

      // If status is null (404), SUCCESS, or PARTIALLY_PROCESSED, we'll proceed to fetch posts
      if (!status || status.sync_status === 'SUCCESS' || status.sync_status === 'PARTIALLY_PROCESSED') {
        // Only call fetchReels if status changed to SUCCESS, PARTIALLY_PROCESSED, or null from something else
        // This prevents unnecessary API calls when status remains the same during polling
        if ((!status || status?.sync_status === 'SUCCESS' || status?.sync_status === 'PARTIALLY_PROCESSED') &&
          previousStatus !== (status?.sync_status || null) &&
          fetchReelsRef.current) {
          fetchReelsRef.current(true);
        }

        // For PARTIALLY_PROCESSED, continue polling to check for completion
        // For SUCCESS or null, stop polling
        const shouldStopPolling = status?.sync_status === 'SUCCESS' || !status;
        return shouldStopPolling;
      }
      return false;
    } catch (error) {
      console.error('Failed to fetch sync status:', error);
      // On error, default to showing posts and stop polling
      return true;
    }
  }, []);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (token) {
      // Initial check
      checkSyncStatus().then((shouldStopPolling) => {
        // Only set up polling if we don't have a successful status yet
        if (!shouldStopPolling) {
          intervalId = setInterval(async () => {
            const shouldStop = await checkSyncStatus();
            if (shouldStop && intervalId) {
              clearInterval(intervalId);
              intervalId = null;
            }
          }, 5000);
        }
      });
    }

    // Cleanup function to clear interval when component unmounts or token changes
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [token]);

  useEffect(() => {
    try {
      // Initialize PostHog and capture the view event
      if (initPostHog()) {
        captureEvent('View Dashboard home');

        trackDashboardViewed();

        // Call updatePostHogIdentity after ensuring PostHog is initialized
        setTimeout(() => {
          updatePostHogIdentity(false);
        }, 100);
      }
    } catch (error) {
      console.error("Error with PostHog:", error);
    }
  }, []);

  // Reusable function to load categories
  const loadCategories = useCallback(async () => {
    try {
      // Load user-specific categories for filtering/display
      const userCategories = await fetchCategories();
      setCategories(userCategories);

      // Load all categories for CategoryChangePopup
      const allCategoriesData = await fetchAllCategories();
      setAllCategories(allCategoriesData);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, []);

  // Reusable function to load post counts
  const loadPostCounts = async () => {
    try {
      const counts = await fetchPostCounts();
      setPostCounts(counts);
    } catch (error) {
      console.error("Error fetching post counts:", error);
    }
  };

  // Reusable function to load platform counts
  const loadPlatformCounts = async () => {
    try {
      const counts = await fetchPlatformCounts();
      setPlatformCounts(counts);
    } catch (error) {
      console.error("Error fetching platform counts:", error);
    }
  };

  const stopAiCategorisationPolling = useCallback(() => {
    if (aiCategorisationPollRef.current) {
      clearInterval(aiCategorisationPollRef.current);
      aiCategorisationPollRef.current = null;
    }
  }, []);

  const checkAiCategorisationStatus = useCallback(async () => {
    try {
      const response = await fetchAiCategorisationStatus();
      const normalizedStatus = response.status?.toUpperCase() ?? null;
      setAiCategorisationStatus(normalizedStatus);

      if (
        normalizedStatus === 'COMPLETED' &&
        previousAiCategorisationStatusRef.current !== 'COMPLETED'
      ) {
        loadCategories();
        setIsAiCategorisationEligibilityLoading(true);
        try {
          const eligibility = await fetchAiCategorisationEligibility();
          setAiCategorisationEligibility(eligibility);
        } catch (error) {
          console.error('Failed to refresh AI categorisation eligibility after completion:', error);
          setAiCategorisationEligibility(null);
        } finally {
          setIsAiCategorisationEligibilityLoading(false);
        }
      }

      previousAiCategorisationStatusRef.current = normalizedStatus;
      return normalizedStatus === 'PENDING' || normalizedStatus === 'PROCESSING';
    } catch (error) {
      console.error('Failed to fetch AI categorisation status:', error);
      return false;
    }
  }, [loadCategories]);

  const startAiCategorisationPolling = useCallback(async () => {
    stopAiCategorisationPolling();
    const shouldContinue = await checkAiCategorisationStatus();
    if (shouldContinue) {
      aiCategorisationPollRef.current = setInterval(async () => {
        const stillRunning = await checkAiCategorisationStatus();
        if (!stillRunning) {
          stopAiCategorisationPolling();
        }
      }, AI_CATEGORISATION_POLL_INTERVAL);
    }
  }, [checkAiCategorisationStatus, stopAiCategorisationPolling]);

  useEffect(() => {
    if (!token) return;

    const initAiCategorisation = async () => {
      setIsAiCategorisationEligibilityLoading(true);
      try {
        const eligibility = await fetchAiCategorisationEligibility();
        setAiCategorisationEligibility(eligibility);
      } catch (error) {
        console.error('Failed to fetch AI categorisation eligibility:', error);
        setAiCategorisationEligibility(null);
      } finally {
        setIsAiCategorisationEligibilityLoading(false);
      }
      await startAiCategorisationPolling();
    };

    initAiCategorisation();

    return () => {
      stopAiCategorisationPolling();
    };
  }, [token, startAiCategorisationPolling, stopAiCategorisationPolling]);

  const handleStartAiCategorisation = async () => {
    setIsStartingAiCategorisation(true);
    try {
      await startAiCategorisation();
      setShowAiCategorisationDialog(false);
      toast.success('We have started your categorisation, once done you will see new categories in the left panel.');
      await startAiCategorisationPolling();
    } catch (error) {
      console.error('Failed to start AI categorisation:', error);
      toast.error('Failed to start categorisation. Please try again.');
    } finally {
      setIsStartingAiCategorisation(false);
    }
  };

  // Export functionality
  const handleExportClick = async () => {
    const isSubscriptionExpired = !subscription || subscription.status !== 'active';
    captureEvent("export_to_sheets_clicked", { "is_active_subscription": !isSubscriptionExpired });
    if (isSubscriptionExpired) {
      setShowExportDialog(true);
      return;
    }
    try {
      const postDumpsStatus = await fetchPostDumpsStatus();
      if (postDumpsStatus.unprocessed_post_dumps_count > 0) {
        setUnprocessedCount(postDumpsStatus.unprocessed_post_dumps_count);
        setShowUnprocessedExportDialog(true);
        return;
      }
    } catch (error) {
      console.error('Failed to fetch post dumps status:', error);
    }
    handleExport();
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportPosts();
      setShowExportSuccessDialog(true);
    } catch (error) {
      console.error('Export failed:', error);
      if (axios.isAxiosError(error)) {
        const detail = error.response?.data?.detail;
        const code = typeof detail?.code === 'string' ? detail.code : null;
        const message = typeof detail?.message === 'string' ? detail.message : null;
        setExportErrorCode(code);
        setExportErrorMessage(message);
      } else {
        setExportErrorCode(null);
        setExportErrorMessage(null);
      }
      setShowExportErrorDialog(true);
    } finally {
      setIsExporting(false);
    }
  };

  const handleUnprocessedProcessNow = async () => {
    setIsProcessingForExport(true);
    try {
      await processPostDumps();
      toast.success('Processing started! Your posts will be available shortly.');
      setShowUnprocessedExportDialog(false);
      setUnprocessedCount(0);
      const updatedSyncStatus = await fetchSyncStatus();
      setSyncStatus(updatedSyncStatus);
    } catch (error) {
      console.error('Failed to start processing:', error);
      toast.error('Failed to start processing. Please try again.');
    } finally {
      setIsProcessingForExport(false);
    }
  };


  // Load categories, post counts, and platform counts once in Home and pass to components
  useEffect(() => {
    if (token) {
      loadCategories();
      loadPostCounts();
      loadPlatformCounts();
    }
  }, [token, loadCategories]);

  // Fetch subscription status for export functionality
  useEffect(() => {
    const loadSubscription = async () => {
      if (token) {
        try {
          const subscriptionData = await fetchSubscription();
          setSubscription(subscriptionData);
        } catch (error) {
          console.error('Failed to load subscription:', error);
          setSubscription(null);
        }
      }
    };

    loadSubscription();
  }, [token]);

  // Start session tracking when component mounts
  useEffect(() => {
    sessionTracker.startSession();

    // Cleanup when component unmounts
    return () => {
      sessionTracker.endSession();
    };
  }, []);

  // Check for extension availability periodically
  useEffect(() => {
    const checkExtension = () => {
      const extensionLoaded = isInstavaultExtensionLoaded();
      setIsExtensionLoaded(extensionLoaded);
    };

    // Check immediately
    checkExtension();

    // Check every 2 seconds
    const interval = setInterval(checkExtension, 2000);

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, []);

  const [reels, setReels] = useState<Post[]>([]);
  const [totalPostsCount, setTotalPostsCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasMore, setHasMore] = useState(true);

  // Bulk selection state — lives here alongside `reels`/pagination so selection survives
  // infinite-scroll appends (fetchReels only ever appends to `reels`, never clears selection).
  const MAX_SELECTION = 100;
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedPostIds, setSelectedPostIds] = useState<Set<number>>(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [isBulkCategoryPopupOpen, setIsBulkCategoryPopupOpen] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState<CategorySelection | null>(null);
  const [selectedPlatform, setSelectedPlatform] = useState("all_platforms");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Keeps header action buttons expanded (not just icon) while their dropdown is open, not only on hover
  const [isImportMenuOpen, setIsImportMenuOpen] = useState(false);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const isInitializing = useRef(true);
  // The scrollable pane posts/grid render inside — masonic's virtualized grid needs this
  // to compute scroll position itself, since the page/window no longer scrolls.
  const mainScrollRef = useRef<HTMLDivElement>(null);

  // Debug menu state changes
  useEffect(() => {

    // Reset initialization flag when menu opens
    if (isMenuOpen) {
      isInitializing.current = true;
      // Set a timeout to mark initialization as complete
      setTimeout(() => {
        isInitializing.current = false;
      }, 100);
    }
  }, [isMenuOpen]);
  const [isSampleData, setIsSampleData] = useState(false); // Track if sample data is used
  const [, setError] = useState<string | null>(null);

  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() => {
    try {
      const stored = localStorage.getItem(HOME_PREFS_SORT_ORDER_KEY);
      if (stored === 'asc' || stored === 'desc') return stored;
    } catch {
      // ignore
    }
    return 'asc';
  });
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    try {
      const stored = localStorage.getItem(HOME_PREFS_VIEW_MODE_KEY);
      if (stored === 'grid' || stored === 'list') return stored;
    } catch {
      // ignore
    }
    return 'grid';
  });

  useEffect(() => {
    localStorage.setItem(HOME_PREFS_SORT_ORDER_KEY, sortOrder);
  }, [sortOrder]);
  useEffect(() => {
    localStorage.setItem(HOME_PREFS_VIEW_MODE_KEY, viewMode);
  }, [viewMode]);

  const [isFilterPopupOpen, setIsFilterPopupOpen] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [postCounts, setPostCounts] = useState<PostCounts>({ favorite_count: 0, archived_count: 0 });
  const [platformCounts, setPlatformCounts] = useState<PlatformCounts>([]);
  const isClearingFiltersRef = useRef(false);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>({
    authorName: '',
    dateFrom: '',
    dateTo: '',
    category: null
  });
  const [viewingArchived, setViewingArchived] = useState(false);
  const [viewingFavorites, setViewingFavorites] = useState(false);
  const [favouriteStates, setFavouriteStates] = useState<{ [key: string]: boolean }>({});

  // Export functionality state
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showUnprocessedExportDialog, setShowUnprocessedExportDialog] = useState(false);
  const [unprocessedCount, setUnprocessedCount] = useState(0);
  const [isProcessingForExport, setIsProcessingForExport] = useState(false);
  const [showExportSuccessDialog, setShowExportSuccessDialog] = useState(false);
  const [showExportErrorDialog, setShowExportErrorDialog] = useState(false);
  const [exportErrorCode, setExportErrorCode] = useState<string | null>(null);
  const [exportErrorMessage, setExportErrorMessage] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [aiCategorisationEligibility, setAiCategorisationEligibility] = useState<AiCategorisationEligibilityResponse | null>(null);
  const [showAiCategorisationDialog, setShowAiCategorisationDialog] = useState(false);
  const [showAiCategorisationUpgradeDialog, setShowAiCategorisationUpgradeDialog] = useState(false);
  const [aiCategorisationStatus, setAiCategorisationStatus] = useState<string | null>(null);
  const [isStartingAiCategorisation, setIsStartingAiCategorisation] = useState(false);
  const aiCategorisationPollRef = useRef<NodeJS.Timeout | null>(null);
  const previousAiCategorisationStatusRef = useRef<string | null>(null);
  const isAiCategorisationInProgress =
    aiCategorisationStatus === 'PENDING' || aiCategorisationStatus === 'PROCESSING';
  const canUseAiCategorisation =
    !!aiCategorisationEligibility?.plan_eligible &&
    (aiCategorisationEligibility.posts_available_to_categorise ?? 0) > 0;
  const needsAiCategorisationPlanUpgrade =
    aiCategorisationEligibility !== null && !aiCategorisationEligibility.plan_eligible;
  const [isExtensionLoaded, setIsExtensionLoaded] = useState(false);
  const [showMcpBanner, setShowMcpBanner] = useState(() => localStorage.getItem('mcpBannerDismissed') !== 'true');

  // Auto-hide the top header (logo + search) when the user scrolls down inside the main
  // pane, reveal it when they scroll back up — frees vertical space while browsing.
  const HEADER_HEIGHT = 80;       // approx collapsed height reclaimed by hiding the header
  const HIDE_ENABLE_MARGIN = 160; // extra scroll room required before hiding is worthwhile
  const SCROLL_DELTA = 6;         // ignore sub-pixel/momentum jitter
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const isHeaderHiddenRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const setHeaderHidden = (hidden: boolean) => {
    if (isHeaderHiddenRef.current === hidden) return;
    isHeaderHiddenRef.current = hidden;
    setIsHeaderHidden(hidden);
  };
  useEffect(() => {
    const el = mainScrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const st = el.scrollTop;
      const last = lastScrollTopRef.current;
      // Measure scrollable distance as if the header were always shown, so the decision
      // never depends on the header's current state — otherwise hiding frees space, kills
      // the overflow, forces a re-show, and the layout oscillates (visible with few posts).
      const shownMaxScroll =
        el.scrollHeight - el.clientHeight + (isHeaderHiddenRef.current ? HEADER_HEIGHT : 0);
      if (shownMaxScroll < HEADER_HEIGHT + HIDE_ENABLE_MARGIN) {
        setHeaderHidden(false); // not enough content for hiding to be stable
        lastScrollTopRef.current = st;
        return;
      }
      if (st - last > SCROLL_DELTA && st > HEADER_HEIGHT) {
        setHeaderHidden(true);  // scrolling down
      } else if (last - st > SCROLL_DELTA) {
        setHeaderHidden(false); // scrolling up
      }
      lastScrollTopRef.current = st;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [initialLoading]);

  const fetchReels = async (reset = false, searchTerm = searchQuery, forceArchived?: boolean, forceFavorites?: boolean) => {
    // If we're clearing filters, don't fetch unless it's the clearing call
    if (isClearingFiltersRef.current && !reset) {
      return;
    }

    if (loading || (!hasMore && !reset)) {
      return;
    }

    // Check if we should be fetching based on sync status
    if (syncStatus && syncStatus.sync_status !== 'SUCCESS' && syncStatus.sync_status !== 'PARTIALLY_PROCESSED') {
      return;
    }

    if (!reset) {
      if (isFetchingReelsRef.current) return;
      isFetchingReelsRef.current = true;
    }

    setLoading(true);
    if (reset) {
      setInitialLoading(true);
    }
    setError(null);
    try {
      const currentPage = reset ? 1 : (1 + Math.floor(reels.length / PAGE_SIZE));

      const currentPlatform = selectedPlatform;

      // Additional safety check - if we're clearing filters, ensure we don't use old values
      if (isClearingFiltersRef.current && reset) {

        // Force the values to be cleared
        const clearedParams = {
          ...(searchTerm ? { search: searchTerm } : {}),
          page: 1, // Always start from page 1 when resetting
          sort_order: sortOrder,
          // No category or platform filters when clearing
          ...((forceArchived !== undefined ? forceArchived : viewingArchived) ? { archived: true } : {}),
          ...((forceFavorites !== undefined ? forceFavorites : viewingFavorites) ? { favorites: true } : {})
        };


        const response = await fetchPosts(clearedParams);
        const { posts: newReels, count } = response;

        if (reset) {
          setReels(newReels);
          setTotalPostsCount(count);
        } else {
          setReels(prev => {
            const existingIds = new Set(prev.map((reel: Post) => reel.id));
            const uniqueNewReels = newReels.filter((reel: Post) => !existingIds.has(reel.id));
            return [...prev, ...uniqueNewReels];
          });
        }

        const hasMoreItems = newReels.length === PAGE_SIZE;
        setHasMore(hasMoreItems);
        setIsSampleData(false);
        return;
      }

      // Build category filter parameters using the new logic
      const categoryFilterParams = buildCategoryFilterParams(selectedCategory);

      const params = {
        ...(searchTerm ? { search: searchTerm } : {}),
        ...(reset ? {} : { page: currentPage }),
        sort_order: sortOrder,
        ...categoryFilterParams,
        ...(currentPlatform !== "all_platforms" ? { platform: getPlatformApiValue(currentPlatform) } : {}),
        ...(appliedFilters.authorName ? { author_name: appliedFilters.authorName } : {}),
        ...(appliedFilters.dateFrom ? { date_from: appliedFilters.dateFrom } : {}),
        ...(appliedFilters.dateTo ? { date_to: appliedFilters.dateTo } : {}),
        ...((forceArchived !== undefined ? forceArchived : viewingArchived) ? { archived: true } : {}),
        ...((forceFavorites !== undefined ? forceFavorites : viewingFavorites) ? { favorites: true } : {})
      };



      const response = await fetchPosts(params);
      const { posts: newReels, count } = response;

      if (reset) {
        setReels(newReels);
        setTotalPostsCount(count);
      } else {
        setReels(prev => {
          // Create a Set of existing post IDs for O(1) lookup
          const existingIds = new Set(prev.map((reel: Post) => reel.id));
          // Filter out new reels that already exist
          const uniqueNewReels = newReels.filter((reel: Post) => !existingIds.has(reel.id));
          return [...prev, ...uniqueNewReels];
        });
      }

      const hasMoreItems = newReels.length === PAGE_SIZE;
      setHasMore(hasMoreItems);
      setIsSampleData(false);
    } catch (error) {
      console.error("[Home] Error fetching reels:", error);
      setError(error instanceof Error ? error.message : "An error occurred");
      setReels(reset ? sampleReels as Post[] : prev => prev.length > 0 ? prev : sampleReels as Post[]);
      setTotalPostsCount(sampleReels.length);
      setIsSampleData(true);
      setHasMore(false); // Stop infinite scrolling when there's an error
    } finally {
      setLoading(false);
      isFetchingReelsRef.current = false;
      if (reset) {
        setInitialLoading(false);
      }
    }
  };

  // Assign fetchReels to ref for use in checkSyncStatus
  useEffect(() => {
    fetchReelsRef.current = fetchReels;
  }, []);

  const handleSearch = (query: string) => {
    // Only perform search if sync status is SUCCESS, PARTIALLY_PROCESSED, or null (404)
    if (syncStatus === null || syncStatus.sync_status === 'SUCCESS' || syncStatus.sync_status === 'PARTIALLY_PROCESSED') {
      if (query.trim()) {
        trackSearchExecuted(query, {
          category: selectedCategory?.id || 0,
          platform: selectedPlatform,
          archived: viewingArchived
        });
      }

      // Reset archived, favorites, and all view when searching
      if (viewingArchived) {
        setViewingArchived(false);
      }
      if (viewingFavorites) {
        setViewingFavorites(false);
      }
      setSearchQuery(query);
      setHasMore(true);
      fetchReels(true, query, false);
    }
  };


  // Effect for sort order and platform changes
  useEffect(() => {
    if (!isClearingFiltersRef.current && (syncStatus === null || syncStatus.sync_status === 'SUCCESS' || syncStatus.sync_status === 'PARTIALLY_PROCESSED')) {
      fetchReels(true);
    }
  }, [sortOrder, selectedPlatform]); // Removed syncStatus from dependencies

  // Separate effect for category changes - only update appliedFilters
  useEffect(() => {

    if (!isClearingFiltersRef.current) {
      // Sync appliedFilters.category with selectedCategory when sidebar changes
      // Check if selectedCategory has changed
      const hasCategoryChanged = selectedCategory && (
        !appliedFilters.category ||
        selectedCategory.id !== appliedFilters.category.id ||
        selectedCategory.category_display_value !== appliedFilters.category.category_display_value ||
        selectedCategory.is_folder !== appliedFilters.category.is_folder
      );

      if (hasCategoryChanged) {
        setAppliedFilters(prev => ({
          ...prev,
          category: selectedCategory
        }));
      } else if (!selectedCategory && appliedFilters.category) {
        setAppliedFilters(prev => ({
          ...prev,
          category: null
        }));
      } else if (!selectedCategory && !appliedFilters.category && (syncStatus === null || syncStatus.sync_status === 'SUCCESS' || syncStatus.sync_status === 'PARTIALLY_PROCESSED')) {
        fetchReels(true);
      } else if (!selectedCategory && !viewingFavorites && !viewingArchived && (syncStatus === null || syncStatus.sync_status === 'SUCCESS' || syncStatus.sync_status === 'PARTIALLY_PROCESSED')) {
        fetchReels(true);
      }
    }
  }, [selectedCategory, viewingFavorites, viewingArchived]);

  // Effect that triggers on appliedFilters changes
  useEffect(() => {
    if (!isClearingFiltersRef.current && (syncStatus === null || syncStatus.sync_status === 'SUCCESS' || syncStatus.sync_status === 'PARTIALLY_PROCESSED')) {
      fetchReels(true);
    }
  }, [appliedFilters]);

  useEffect(() => {
    if (!isClearingFiltersRef.current && (syncStatus === null || syncStatus.sync_status === 'SUCCESS' || syncStatus.sync_status === 'PARTIALLY_PROCESSED')) {
      if (!viewingArchived && !selectedCategory && !appliedFilters.category) {
        fetchReels(true);
      }
    }
  }, [viewingArchived, selectedCategory, appliedFilters.category]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) { // md breakpoint is 768px in Tailwind by default
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, [isMenuOpen]);

  // Function to handle loading more reels when user scrolls to bottom
  const handleLoadMore = () => {
    if (!loading && hasMore && (syncStatus === null || syncStatus.sync_status === 'SUCCESS' || syncStatus.sync_status === 'PARTIALLY_PROCESSED')) {
      fetchReels(false);
    }
  };

  const handleApplyFilters = useCallback((filters: FilterState) => {
    // Reset archived, favorites, and all view when applying any filters
    if (viewingArchived) {
      setViewingArchived(false);
    }
    if (viewingFavorites) {
      setViewingFavorites(false);
    }

    // If all filters are cleared, clear platform and category immediately
    if (!filters.authorName && !filters.dateFrom && !filters.dateTo && !filters.category) {

      // Clear all state immediately
      setSelectedPlatform("all_platforms");
      setSelectedCategory(null);
      setSearchQuery(''); // Clear search query as well
      setAppliedFilters(filters);

      // Call the cleared fetch function directly
      fetchReelsCleared();
    } else {
      // Sync the sidebar category with the filter category
      if (filters.category) {
        setSelectedCategory(filters.category);
      } else {
        setSelectedCategory(null);
      }

      // Set the applied filters for non-clearing cases
      setAppliedFilters(filters);
    }
  }, [selectedPlatform, selectedCategory, viewingArchived]);

  const handleCloseFilterPopup = useCallback(() => {
    setIsFilterPopupOpen(false);
  }, []);

  const getAppliedFiltersCount = () => {
    let count = 0;
    if (appliedFilters.authorName) count++;
    if (appliedFilters.dateFrom || appliedFilters.dateTo) count++;
    if (appliedFilters.category) count++;
    return count;
  };

  // Helper function to check if any filters are applied
  const hasFiltersApplied = () => {
    return !!(
      appliedFilters.authorName ||
      appliedFilters.dateFrom ||
      appliedFilters.dateTo ||
      appliedFilters.category ||
      selectedPlatform !== "all_platforms" ||
      selectedCategory !== null
    );
  };

  // Check if only a specific platform is selected with no category (no other filters)
  const isOnlyPlatformSelected = () => {
    return selectedPlatform !== "all_platforms" &&
      selectedCategory === null &&
      !appliedFilters.authorName &&
      !appliedFilters.dateFrom &&
      !appliedFilters.dateTo &&
      !appliedFilters.category &&
      !viewingArchived &&
      !viewingFavorites;
  };

  // Function to clear all filters
  const handleClearAllFilters = () => {

    const clearedFilters: FilterState = {
      authorName: '',
      dateFrom: '',
      dateTo: '',
      category: null
    };

    // Clear all state immediately
    setSelectedCategory(null);
    setSelectedPlatform("all_platforms");
    setSearchQuery('');
    setViewingArchived(false);
    setViewingFavorites(false);
    setHasMore(true);
    setTotalPostsCount(0);
    setAppliedFilters(clearedFilters);

    // Call fetchReels directly with cleared parameters
    fetchReelsCleared();
  };

  // Separate function to fetch reels with cleared parameters
  const fetchReelsCleared = async () => {

    if (loading) {
      return;
    }

    // Check if we should be fetching based on sync status
    if (syncStatus && syncStatus.sync_status !== 'SUCCESS' && syncStatus.sync_status !== 'PARTIALLY_PROCESSED') {
      return;
    }

    setLoading(true);
    setInitialLoading(true);
    setError(null);

    try {
      const clearedParams = {
        page: 1,
        sort_order: sortOrder,
        // No filters - all cleared
      };

      const response = await fetchPosts(clearedParams);
      const { posts: newReels, count } = response;

      setReels(newReels);
      setTotalPostsCount(count);

      const hasMoreItems = newReels.length === PAGE_SIZE;
      setHasMore(hasMoreItems);
      setIsSampleData(false);

    } catch (error) {
      setError(error instanceof Error ? error.message : "An error occurred");
      setReels([]);
      setTotalPostsCount(0);
      setIsSampleData(false);
      setHasMore(false);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const handleNoteUpdate = (postId: number, note: string) => {
    setReels(prevReels =>
      prevReels.map(reel =>
        reel.id === postId ? { ...reel, note } : reel
      )
    );
  };

  const handleCategoryUpdate = (postId: number, category?: any) => {
    setReels(prevReels =>
      prevReels.map(reel => {
        if (reel.id === postId) {
          // Use the provided category object if available, otherwise create a fallback
          if (category) {
            return { ...reel, category };
          }
        }
        return reel;
      })
    );
    // Reload categories, post counts, and platform counts to update counts
    loadCategories();
    loadPostCounts();
    loadPlatformCounts();
  };

  const handlePostDelete = (postId: number) => {
    setReels(prevReels => prevReels.filter(reel => reel.id !== postId));
    setTotalPostsCount(prev => Math.max(0, prev - 1));
    // Reload categories, post counts, and platform counts to update counts
    loadCategories();
    loadPostCounts();
    loadPlatformCounts();
  };

  const handlePostArchive = (postId: number) => {
    // Remove the post from the current list when archiving/unarchiving
    setReels(prevReels => prevReels.filter(reel => reel.id !== postId));
    setTotalPostsCount(prev => Math.max(0, prev - 1));
    // Reload categories, post counts, and platform counts to update counts
    loadCategories();
    loadPostCounts();
    loadPlatformCounts();
  };

  const handleFavouriteToggle = async (postId: number, isFavourite: boolean) => {
    try {

      const { markPostfavorite } = await import('../services/api');
      await markPostfavorite(postId, isFavourite);

      // Update the local state
      setFavouriteStates(prev => ({
        ...prev,
        [postId.toString()]: isFavourite
      }));

      // Update the post in the reels array
      setReels(prevReels => {
        const updatedReels = prevReels.map(reel =>
          reel.id === postId ? { ...reel, is_favorite: isFavourite } : reel
        );

        // If we're viewing favorites and unfavouriting, remove the post from the list
        if (viewingFavorites && !isFavourite) {
          const filteredReels = updatedReels.filter(reel => reel.id !== postId);
          setTotalPostsCount(prev => Math.max(0, prev - 1));
          return filteredReels;
        }

        return updatedReels;
      });

      // Reload categories, post counts, and platform counts to update counts (in case favorites affect category counts)
      loadCategories();
      loadPostCounts();
      loadPlatformCounts();
    } catch (error) {
      console.error('Failed to toggle favourite:', error);
      // Optionally show a toast error here
    }
  };

  // --- Bulk selection ---------------------------------------------------

  const handleToggleSelectionMode = () => {
    setSelectionMode(prev => {
      const next = !prev;
      if (!next) {
        // Leaving selection mode clears the selection
        setSelectedPostIds(new Set());
      }
      return next;
    });
  };

  // Anchor for shift-click range selection: the id of the last post toggled on its own.
  const lastSelectedIdRef = useRef<number | null>(null);

  const handleToggleSelectPost = (postId: number, shiftKey = false) => {
    // Shift-click: select every post between the anchor and this one (in display order).
    if (shiftKey && lastSelectedIdRef.current !== null && lastSelectedIdRef.current !== postId) {
      const ids = reels.map(r => r.id);
      const anchorIdx = ids.indexOf(lastSelectedIdRef.current);
      const currentIdx = ids.indexOf(postId);
      if (anchorIdx !== -1 && currentIdx !== -1) {
        const [lo, hi] = anchorIdx < currentIdx ? [anchorIdx, currentIdx] : [currentIdx, anchorIdx];
        const rangeIds = ids.slice(lo, hi + 1);
        setSelectedPostIds(prev => {
          const next = new Set(prev);
          let hitLimit = false;
          for (const id of rangeIds) {
            if (next.has(id)) continue;
            if (next.size >= MAX_SELECTION) { hitLimit = true; break; }
            next.add(id);
          }
          if (hitLimit) toast.error(`You can select up to ${MAX_SELECTION} posts at a time.`);
          return next;
        });
        lastSelectedIdRef.current = postId;
        return;
      }
    }

    setSelectedPostIds(prev => {
      if (prev.has(postId)) {
        const next = new Set(prev);
        next.delete(postId);
        return next;
      }
      if (prev.size >= MAX_SELECTION) {
        toast.error(`You can select up to ${MAX_SELECTION} posts at a time.`);
        return prev;
      }
      const next = new Set(prev);
      next.add(postId);
      return next;
    });
    lastSelectedIdRef.current = postId;
  };

  const handleClearSelection = () => {
    setSelectedPostIds(new Set());
  };

  const handleBulkActionSettled = (succeededIds: number[]) => {
    // Common bookkeeping after any bulk action completes
    const succeededSet = new Set(succeededIds);
    setSelectedPostIds(prev => {
      const next = new Set(prev);
      succeededIds.forEach(id => next.delete(id));
      return next;
    });
    // Nothing left selected → leave selection mode automatically. Anything remaining is a
    // failed item the user may want to retry, so keep the mode on in that case.
    const allCleared = Array.from(selectedPostIds).every(id => succeededSet.has(id));
    if (allCleared) {
      setSelectionMode(false);
    }
    loadCategories();
    loadPostCounts();
    loadPlatformCounts();
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedPostIds);
    if (ids.length === 0 || isBulkProcessing) return;

    setIsBulkProcessing(true);
    try {
      const response = await bulkDeletePosts(ids);
      const failedSet = new Set(response.failed_ids);
      const succeededIds = ids.filter(id => !failedSet.has(id));

      setReels(prev => prev.filter(reel => !succeededIds.includes(reel.id)));
      setTotalPostsCount(prev => Math.max(0, prev - succeededIds.length));
      handleBulkActionSettled(succeededIds);

      if (response.failed_ids.length > 0) {
        toast.error(`Deleted ${response.updated_count} post(s). Failed to delete ${response.failed_ids.length}: ${response.failed_ids.join(', ')}`);
      } else {
        toast.success(`Deleted ${response.updated_count} post(s)`);
      }
    } catch (error) {
      console.error('Bulk delete failed:', error);
      toast.error('Failed to delete selected posts. Please try again.');
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const handleBulkArchive = async (archive: boolean) => {
    const ids = Array.from(selectedPostIds);
    if (ids.length === 0 || isBulkProcessing) return;

    setIsBulkProcessing(true);
    try {
      const response = await bulkArchivePosts(ids, archive);
      const failedSet = new Set(response.failed_ids);
      const succeededIds = ids.filter(id => !failedSet.has(id));

      // Mirror single-post archive/unarchive: remove affected posts from the current list
      setReels(prev => prev.filter(reel => !succeededIds.includes(reel.id)));
      setTotalPostsCount(prev => Math.max(0, prev - succeededIds.length));
      handleBulkActionSettled(succeededIds);

      const verb = archive ? 'Archived' : 'Unarchived';
      if (response.failed_ids.length > 0) {
        toast.error(`${verb} ${response.updated_count} post(s). Failed for ${response.failed_ids.length}: ${response.failed_ids.join(', ')}`);
      } else {
        toast.success(`${verb} ${response.updated_count} post(s)`);
      }
    } catch (error) {
      console.error('Bulk archive failed:', error);
      toast.error('Failed to update selected posts. Please try again.');
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const handleBulkFavourite = async (isFavorite: boolean) => {
    const ids = Array.from(selectedPostIds);
    if (ids.length === 0 || isBulkProcessing) return;

    setIsBulkProcessing(true);
    try {
      const response = await bulkMarkFavorite(ids, isFavorite);
      const failedSet = new Set(response.failed_ids);
      const succeededIds = ids.filter(id => !failedSet.has(id));
      const succeededSet = new Set(succeededIds);

      setFavouriteStates(prev => {
        const next = { ...prev };
        succeededIds.forEach(id => { next[id.toString()] = isFavorite; });
        return next;
      });

      setReels(prev => {
        const updated = prev.map(reel =>
          succeededSet.has(reel.id) ? { ...reel, is_favorite: isFavorite } : reel
        );

        if (viewingFavorites && !isFavorite) {
          const filtered = updated.filter(reel => !succeededSet.has(reel.id));
          setTotalPostsCount(p => Math.max(0, p - succeededIds.length));
          return filtered;
        }

        return updated;
      });

      handleBulkActionSettled(succeededIds);

      const verb = isFavorite ? 'Marked' : 'Unmarked';
      if (response.failed_ids.length > 0) {
        toast.error(`${verb} ${response.updated_count} post(s) as favourite. Failed for ${response.failed_ids.length}: ${response.failed_ids.join(', ')}`);
      } else {
        toast.success(`${verb} ${response.updated_count} post(s) as favourite`);
      }
    } catch (error) {
      console.error('Bulk favourite update failed:', error);
      toast.error('Failed to update selected posts. Please try again.');
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const handleBulkCategoryUpdate = (postIds: number[], category: any, failedIds: number[]) => {
    const failedSet = new Set(failedIds);
    const succeededIds = postIds.filter(id => !failedSet.has(id));
    const succeededSet = new Set(succeededIds);

    setReels(prev => prev.map(reel => succeededSet.has(reel.id) ? { ...reel, category } : reel));
    setIsBulkCategoryPopupOpen(false);
    handleBulkActionSettled(succeededIds);

    if (failedIds.length > 0) {
      toast.error(`Updated category for ${succeededIds.length} post(s). Failed for ${failedIds.length}: ${failedIds.join(', ')}`);
    } else {
      toast.success(`Updated category for ${succeededIds.length} post(s)`);
    }
  };

  // Helper function to get selected category name
  const getSelectedCategoryName = () => {
    if (!selectedCategory) return null;
    return selectedCategory.category_display_value;
  };

  const handleViewArchived = () => {
    if (!viewingArchived) {
      // Track archived posts viewed
      trackArchivedPostsViewed();

      setViewingArchived(true);
      setViewingFavorites(false); // Reset favorites view
      setSelectedPlatform("all_platforms");
      setSelectedCategory(null);
      setSearchQuery('');
      setAppliedFilters({
        authorName: '',
        dateFrom: '',
        dateTo: '',
        category: null
      });
      setHasMore(true);
      fetchReels(true, '', true, false);
    }
    // Close menu on mobile when viewing archived
    if (isMenuOpen && !isInitializing.current) {
      setIsMenuOpen(false);
    }
    // Scroll to top when viewing archived
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewFavorites = () => {
    if (!viewingFavorites) {
      trackFavouritePostsViewed();

      setViewingFavorites(true);
      setViewingArchived(false); // Reset archived view
      setSelectedPlatform("all_platforms");
      setSelectedCategory(null);
      setSearchQuery('');
      setAppliedFilters({
        authorName: '',
        dateFrom: '',
        dateTo: '',
        category: null
      });
      setHasMore(true);
      fetchReels(true, '', false, true); // Pass true for favorites
    }
    // Close menu on mobile when viewing favorites
    if (isMenuOpen && !isInitializing.current) {
      setIsMenuOpen(false);
    }
    // Scroll to top when viewing favorites
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Function to render main content based on sync status
  const renderMainContent = () => {
    if (syncStatus && (syncStatus.sync_status === 'PENDING' || syncStatus.sync_status === 'PROCESSING')) {
      return <SyncStatus status={syncStatus.sync_status} />;
    }

    return (
      <>
        {/* Processing indicator for PARTIALLY_PROCESSED status */}
        {syncStatus && syncStatus.sync_status === 'PARTIALLY_PROCESSED' && (
          <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <Loader className="text-blue-300 size-sm" />
              </div>
              <div className="flex-1">
                <h3 className="text-blue-300 font-medium mb-1">Posts are still processing</h3>
                <p className="text-blue-200/80 text-sm">
                  Some of your posts are available now, while others are still being processed.
                  We'll update this page automatically when processing is complete.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Rewind 2025 Banner - Only show when not processing and before end date */}
        {totalPostsCount > 0 && 
         new Date() <= REWIND_END_DATE &&
         (!syncStatus || 
          (syncStatus.sync_status !== 'PENDING' && 
           syncStatus.sync_status !== 'PROCESSING' && 
           syncStatus.sync_status !== 'PARTIALLY_PROCESSED')) && (
          <div className="mb-6 mt-4 sm:mt-6 bg-gradient-to-r from-purple-500/20 to-purple-600/20 border border-purple-500/30 rounded-lg p-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <Gift className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-white font-medium text-sm sm:text-base">
                  Your Instavault Rewind for 2025 is ready.
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/rewind25')}
              className="bg-purple-500 hover:bg-purple-600 text-white font-medium px-4 py-2 rounded-lg transition-colors flex-shrink-0"
            >
              View now
            </Button>
          </div>
        )}

        {/* Your Bookmarks Header */}

        {/* Controls */}
        <div className="mb-6">
          {/* Desktop Layout - Title/Count and Buttons in same row */}
          <div className="hidden md:flex items-end justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {viewingArchived ? "Archived Posts" :
                  viewingFavorites ? "Your Favourites" :
                    getSelectedCategoryName() ? getSelectedCategoryName() : "Your Saved Posts"}
              </h2>
              <p className="text-base text-gray-400">
                {totalPostsCount} {viewingArchived ? "archived posts" : viewingFavorites ? "favourite posts" : "saved posts"}
              </p>
            </div>

            {/* Action Buttons - Desktop Only */}
            <div className="flex flex-row items-center gap-2 overflow-visible">

              {/* Bulk selection toggle — hidden when the user has no real posts here
                  (sample/placeholder data doesn't count) */}
              {reels.length > 0 && !isSampleData && (
                <Button
                  variant="outline"
                  className={`group flex items-center gap-0 border-gray-600 text-white hover:bg-gray-600 px-2 ${selectionMode ? 'bg-purple-600 hover:bg-purple-700 border-purple-600' : 'bg-gray-700'}`}
                  disabled={loading}
                  onClick={handleToggleSelectionMode}
                  title={selectionMode ? 'Cancel Selection' : 'Select'}
                >
                  <CheckSquare className="h-4 w-4 shrink-0" />
                  <span className={`overflow-hidden whitespace-nowrap transition-all duration-300 group-hover:max-w-[160px] group-hover:opacity-100 group-hover:ml-2 ${selectionMode ? 'max-w-[160px] opacity-100 ml-2' : 'max-w-0 opacity-0'}`}>
                    {selectionMode ? 'Cancel Selection' : 'Select'}
                  </span>
                </Button>
              )}

              {/* Import/Install Extension Button - Desktop Only */}
              {isExtensionLoaded ? (
                <DropdownMenu open={isImportMenuOpen} onOpenChange={setIsImportMenuOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="group flex items-center gap-0 bg-gray-700 border-gray-600 text-white hover:bg-gray-600 px-2"
                      disabled={loading}
                      title="Import"
                    >
                      <svg
                        className="w-4 h-4 shrink-0 animate-pulse"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                        />
                      </svg>
                      <span className={`overflow-hidden whitespace-nowrap transition-all duration-300 group-hover:max-w-[160px] group-hover:opacity-100 group-hover:ml-2 ${isImportMenuOpen ? 'max-w-[160px] opacity-100 ml-2' : 'max-w-0 opacity-0'}`}>
                        Import
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-gray-700 border-gray-600">
                    <DropdownMenuItem
                      className="text-white hover:bg-gray-600 cursor-pointer"
                      onClick={() => window.open("https://instagram.com?iv=true", "_blank")}
                    >
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                      </svg>
                      Import from Instagram
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-white hover:bg-gray-600 cursor-pointer"
                      onClick={() => window.open("https://x.com?iv=true", "_blank")}
                    >
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                      </svg>
                      Import from X
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-white hover:bg-gray-600 cursor-pointer"
                      onClick={() => window.open("https://linkedin.com/feed?iv=true", "_blank")}
                    >
                      <Linkedin className="w-4 h-4 mr-2" />
                      Import from LinkedIn
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-white hover:bg-gray-600 cursor-pointer"
                      onClick={() => window.open("https://tiktok.com?iv=true", "_blank")}
                    >
                      <TikTokIcon className="w-4 h-4 mr-2" />
                      Import from TikTok
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-white hover:bg-gray-600 cursor-pointer"
                      onClick={() => window.open("https://reddit.com?iv=true", "_blank")}
                    >
                      <RedditIcon className="w-4 h-4 mr-2" />
                      Import from Reddit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-white hover:bg-gray-600 cursor-pointer"
                      onClick={() => syncChromeBookmarks()}
                    >
                      <ChromeIcon className="w-4 h-4 mr-2"/>
                      Import Chrome Bookmarks
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  variant="outline"
                  className="group flex items-center gap-0 bg-gray-700 border-gray-600 text-white hover:bg-gray-600 px-2"
                  disabled={loading}
                  onClick={() => window.open("https://chromewebstore.google.com/detail/instavault-smart-instagra/mmcjnocpldjdigmcplbckhlbjeeclnbp", "_blank")}
                  title="Install Extension"
                >
                  <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 496 512" className="w-4 h-4 shrink-0" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                    <path d="M131.5 217.5L55.1 100.1c47.6-59.2 119-91.8 192-92.1 42.3-.3 85.5 10.5 124.8 33.2 43.4 25.2 76.4 61.4 97.4 103L264 133.4c-58.1-3.4-113.4 29.3-132.5 84.1zm32.9 38.5c0 46.2 37.4 83.6 83.6 83.6s83.6-37.4 83.6-83.6-37.4-83.6-83.6-83.6-83.6 37.3-83.6 83.6zm314.9-89.2L339.6 174c37.9 44.3 38.5 108.2 6.6 157.2L234.1 503.6c46.5 2.5 94.4-7.7 137.8-32.9 107.4-62 150.9-192 107.4-303.9zM133.7 303.6L40.4 120.1C14.9 159.1 0 205.9 0 256c0 124 90.8 226.7 209.5 244.9l63.7-124.8c-57.6 10.8-113.2-20.8-139.5-72.5z"></path>
                  </svg>
                  <span className="max-w-0 overflow-hidden opacity-0 whitespace-nowrap transition-all duration-300 group-hover:max-w-[160px] group-hover:opacity-100 group-hover:ml-2">
                    Install Extension
                  </span>
                </Button>
              )}

              {/* Export Dropdown - only show when there are posts */}
              {reels.length > 0 && (
                <DropdownMenu open={isExportMenuOpen} onOpenChange={setIsExportMenuOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className={`group flex items-center gap-0 bg-gray-700 border-gray-600 text-white hover:bg-gray-600 px-2 ${isExporting ? 'animate-pulse' : ''}`}
                      disabled={loading || isExporting}
                      title={isExporting ? 'Exporting...' : 'Export'}
                    >
                      {isExporting ? (
                        <svg
                          className="w-4 h-4 shrink-0 animate-spin"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                      ) : (
                        <Upload className="shrink-0" />
                      )}
                      <span className={`flex items-center gap-1 overflow-hidden whitespace-nowrap transition-all duration-300 group-hover:max-w-[160px] group-hover:opacity-100 group-hover:ml-2 ${isExportMenuOpen ? 'max-w-[160px] opacity-100 ml-2' : 'max-w-0 opacity-0'}`}>
                        {isExporting ? 'Exporting...' : 'Export'}
                        <ChevronDown className="h-4 w-4 shrink-0" />
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-gray-700 border-gray-600">
                    <DropdownMenuItem
                      onClick={handleExportClick}
                      disabled={loading || isExporting}
                      className="text-white hover:bg-gray-600 cursor-pointer"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Export to Sheets
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate('/profile?active=integrations')}
                      className="text-white hover:bg-gray-600 cursor-pointer"
                    >
                      <Database className="h-4 w-4 mr-2" />
                      Export to Notion
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {reels.length > 0 && isAiCategorisationEligibilityLoading && (
                <Button
                  variant="outline"
                  className="group flex items-center gap-0 bg-gray-700 border-gray-600 text-white hover:bg-gray-600 px-2"
                  disabled
                  title="Advanced AI Categorisation"
                >
                  <svg
                    className="w-4 h-4 shrink-0 animate-spin"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  <span className="max-w-0 overflow-hidden opacity-0 whitespace-nowrap transition-all duration-300 group-hover:max-w-[220px] group-hover:opacity-100 group-hover:ml-2">
                    Advanced AI Categorisation
                  </span>
                </Button>
              )}

              {reels.length > 0 && !isAiCategorisationEligibilityLoading && canUseAiCategorisation && (
                <div className="relative inline-flex shrink-0 overflow-visible">
                  <Button
                    variant="outline"
                    className="group flex items-center gap-0 bg-gray-700 border-gray-600 text-white hover:bg-gray-600 px-2"
                    disabled={loading || isAiCategorisationInProgress}
                    onClick={() => setShowAiCategorisationDialog(true)}
                    title={isAiCategorisationInProgress ? 'Categorising your posts' : 'Advanced AI Categorisation'}
                  >
                    {isAiCategorisationInProgress ? (
                      <svg
                        className="h-4 w-4 shrink-0 animate-spin text-white"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden
                      >
                        <circle
                          className="opacity-30"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-95"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    ) : (
                      <Sparkles className="h-4 w-4 shrink-0" />
                    )}
                    <span className="max-w-0 overflow-hidden opacity-0 whitespace-nowrap transition-all duration-300 group-hover:max-w-[220px] group-hover:opacity-100 group-hover:ml-2">
                      {isAiCategorisationInProgress ? 'Categorising your posts' : 'Advanced AI Categorisation'}
                    </span>
                  </Button>
                  {!isAiCategorisationInProgress && <NewSidebarBadge className="absolute right-2 top-0 z-20 -translate-y-1/2" />}
                </div>
              )}

              {reels.length > 0 && !isAiCategorisationEligibilityLoading && needsAiCategorisationPlanUpgrade && (
                <Button
                  variant="outline"
                  className="group flex items-center gap-0 bg-gray-700 border-gray-600 text-white hover:bg-gray-600 px-2"
                  disabled={loading}
                  onClick={() => setShowAiCategorisationUpgradeDialog(true)}
                  title="Advanced ai categorisation"
                >
                  <Sparkles className="h-4 w-4 shrink-0" />
                  <span className="max-w-0 overflow-hidden opacity-0 whitespace-nowrap transition-all duration-300 group-hover:max-w-[220px] group-hover:opacity-100 group-hover:ml-2">
                    Advanced ai categorisation
                  </span>
                </Button>
              )}

              <DropdownMenu open={isSortMenuOpen} onOpenChange={setIsSortMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="group flex items-center gap-0 bg-gray-700 border-gray-600 text-white hover:bg-gray-600 px-2"
                    disabled={loading}
                    title={sortOrder === 'desc' ? 'Newest First' : 'Oldest First'}
                  >
                    <ArrowUpDown className="h-4 w-4 shrink-0" />
                    <span className={`overflow-hidden whitespace-nowrap transition-all duration-300 group-hover:max-w-[160px] group-hover:opacity-100 group-hover:ml-2 ${isSortMenuOpen ? 'max-w-[160px] opacity-100 ml-2' : 'max-w-0 opacity-0'}`}>
                      {sortOrder === 'desc' ? 'Newest First' : 'Oldest First'}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-gray-700 border-gray-600">
                  <DropdownMenuRadioGroup value={sortOrder} onValueChange={(value) => {
                    const newSortOrder = value as 'asc' | 'desc';
                    trackSortOrderChanged(newSortOrder);
                    setSortOrder(newSortOrder);
                  }}>
                    <DropdownMenuRadioItem value="desc" className="text-white hover:bg-gray-600">
                      Newest First
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="asc" className="text-white hover:bg-gray-600">
                      Oldest First
                    </DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
              <div className="flex bg-gray-700 rounded-lg p-1">
                <button
                  className={`p-2 rounded transition-colors ${viewMode === 'grid' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'}`}
                  onClick={() => setViewMode('grid')}
                  title="Grid View"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  className={`p-2 rounded transition-colors ${viewMode === 'list' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'}`}
                  onClick={() => setViewMode('list')}
                  title="List View"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Layout - Title and Post Count */}
          <div className="md:hidden mb-4">
            <h2 className="text-xl font-bold text-white mb-2">
              {viewingArchived ? "Archived Posts" :
                viewingFavorites ? "Your Favourites" :
                  getSelectedCategoryName() ? getSelectedCategoryName() : "Your Saved Posts"}
            </h2>
            <p className="text-sm text-gray-400">
              {totalPostsCount} {viewingArchived ? "archived posts" : viewingFavorites ? "favourite posts" : "saved posts"}
            </p>
          </div>
        </div>

        {/* Unprocessed Posts Notification - only show when sync status is SUCCESS */}
        {syncStatus && syncStatus.sync_status === 'SUCCESS' && (
          <UnprocessedPostsNotification onProcessNow={checkSyncStatus} />
        )}

        {/* Bulk Action Toolbar - only shown while posts are selected */}
        <BulkActionToolbar
          selectedCount={selectedPostIds.size}
          maxSelection={MAX_SELECTION}
          isProcessing={isBulkProcessing}
          viewingArchived={viewingArchived}
          onDelete={handleBulkDelete}
          onArchive={handleBulkArchive}
          onFavourite={handleBulkFavourite}
          onAssignCategory={() => setIsBulkCategoryPopupOpen(true)}
          onClearSelection={handleClearSelection}
          scrollContainerRef={mainScrollRef}
        />

        {reels.length > 0 ? (
          <ReelGrid
            scrollContainerRef={mainScrollRef}
            reels={reels}
            loading={loading}
            initialLoading={initialLoading}
            isBlurred={isSampleData}
            onLoadMore={handleLoadMore}
            hasMore={hasMore}
            viewMode={viewMode}
            onNoteUpdate={handleNoteUpdate}
            onCategoryUpdate={handleCategoryUpdate}
            onPostDelete={handlePostDelete}
            onPostArchive={handlePostArchive}
            favouriteStates={favouriteStates}
            onFavouriteToggle={handleFavouriteToggle}
            categories={allCategories}
            selectionMode={selectionMode}
            selectedPostIds={selectedPostIds}
            onToggleSelect={handleToggleSelectPost}
          />
        ) : reels.length === 0 && !loading ? (
          <div className="flex flex-col justify-center items-center h-[calc(100vh-300px)] gap-4">
            {/* Lottie animation for no data */}
            <div className="w-48 h-48 md:w-64 md:h-64 mb-2 md:mb-4">
              <Lottie
                animationData={nodataAnimation}
                loop={true}
                autoplay={true}
                style={{ width: '100%', height: '100%' }}
              />
            </div>
            <p className="text-white text-lg">
              {searchQuery || hasFiltersApplied() || viewingArchived || viewingFavorites
                ? "No posts found with current set of filters"
                : "No posts imported yet"}
            </p>

            {/* Mobile/Tablet only note about desktop sync - only show when clear filters button is not visible */}
            {!((searchQuery || hasFiltersApplied() || viewingArchived || viewingFavorites) && !isOnlyPlatformSelected()) && (
              <div className="md:hidden bg-purple-500/20 text-purple-300 border border-purple-500/30 rounded-lg p-4 mx-4 max-w-md">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-purple-300 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-sm text-purple-300 font-medium">Continue on desktop</p>
                    <p className="text-xs text-purple-300 mt-1">
                      For your security, You can sync your saves only on your desktop. Once the sync is complete, you’ll be able to use Instavault on your mobile as well.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {(searchQuery || hasFiltersApplied() || viewingArchived || viewingFavorites) && !isOnlyPlatformSelected() ? (
              <Button
                size="lg"
                className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-full text-lg font-semibold mt-8"
                onClick={handleClearAllFilters}
              >
                Clear All Filters
              </Button>
            ) : isOnlyPlatformSelected() && isExtensionLoaded ? (
              <div className="flex flex-col sm:flex-row gap-4 md:mt-8 mt-2">
                {selectedPlatform === "instagram" && (
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white px-8 py-3 rounded-full text-lg font-semibold"
                    onClick={() => window.open("https://instagram.com?iv=true", "_blank")}
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                    Import from Instagram
                  </Button>
                )}
                {selectedPlatform === "twitter" && (
                  <Button
                    size="lg"
                    className="bg-black hover:bg-gray-800 text-white px-8 py-3 rounded-full text-lg font-semibold"
                    onClick={() => window.open("https://x.com?iv=true", "_blank")}
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                    Import from X
                  </Button>
                )}
                {selectedPlatform === "linkedin" && (
                  <div className="relative">
                    <Button
                      size="lg"
                      className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-full text-lg font-semibold"
                      onClick={() => window.open("https://linkedin.com/feed?iv=true", "_blank")}
                    >
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                        <rect width="4" height="12" x="2" y="9"></rect>
                        <circle cx="4" cy="4" r="2"></circle>
                      </svg>
                      Import from LinkedIn
                    </Button>
                  </div>
                )}
                {selectedPlatform === "tiktok" && (
                  <div className="relative">
                    <Button
                      size="lg"
                      className="bg-black hover:bg-black-700 text-white px-8 py-3 rounded-full text-lg font-semibold"
                      onClick={() => window.open("https://tiktok.com?iv=true", "_blank")}
                    >
                      <TikTokIcon className="w-5 h-5 mr-2" />
                      Import from TikTok
                    </Button>
                  </div>
                )}
                {selectedPlatform === "reddit" && (
                  <div className="relative">
                    <Button
                      size="lg"
                      className="bg-[#FF4500] hover:bg-[#e03d00] text-white px-8 py-3 rounded-full text-lg font-semibold"
                      onClick={() => window.open("https://reddit.com?iv=true", "_blank")}
                    >
                      <RedditIcon className="w-5 h-5 mr-2" />
                      Import from Reddit
                    </Button>
                  </div>
                )}
                {selectedPlatform === "chrome_bookmark" && (
                  <div className="relative">
                    <Button
                      size="lg"
                      className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-3 rounded-full text-lg font-semibold"
                      onClick={() => syncChromeBookmarks()}
                    >
                        <ChromeIcon />
                      Import chrome bookmarks
                    </Button>
                  </div>
                )}
              </div>
            ) : isExtensionLoaded ? (
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white px-8 py-3 rounded-full text-lg font-semibold"
                  onClick={() => window.open("https://instagram.com?iv=true", "_blank")}
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                  Import from Instagram
                </Button>
                <Button
                  size="lg"
                  className="bg-black hover:bg-gray-800 text-white px-8 py-3 rounded-full text-lg font-semibold"
                  onClick={() => window.open("https://x.com?iv=true", "_blank")}
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                  Import from X
                </Button>
                <div className="relative">
                  <Button
                    size="lg"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-full text-lg font-semibold"
                    onClick={() => window.open("https://linkedin.com/feed?iv=true", "_blank")}
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                      <rect width="4" height="12" x="2" y="9"></rect>
                      <circle cx="4" cy="4" r="2"></circle>
                    </svg>
                    Import from LinkedIn
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                size="lg"
                className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-full text-lg font-semibold mt-2 md:mt-8"
                onClick={() => window.open("https://chromewebstore.google.com/detail/instavault-smart-instagra/mmcjnocpldjdigmcplbckhlbjeeclnbp", "_blank")}
              >
                <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 496 512" className="inline-block w-5 h-5 ml-1" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M131.5 217.5L55.1 100.1c47.6-59.2 119-91.8 192-92.1 42.3-.3 85.5 10.5 124.8 33.2 43.4 25.2 76.4 61.4 97.4 103L264 133.4c-58.1-3.4-113.4 29.3-132.5 84.1zm32.9 38.5c0 46.2 37.4 83.6 83.6 83.6s83.6-37.4 83.6-83.6-37.4-83.6-83.6-83.6-83.6 37.3-83.6 83.6zm314.9-89.2L339.6 174c37.9 44.3 38.5 108.2 6.6 157.2L234.1 503.6c46.5 2.5 94.4-7.7 137.8-32.9 107.4-62 150.9-192 107.4-303.9zM133.7 303.6L40.4 120.1C14.9 159.1 0 205.9 0 256c0 124 90.8 226.7 209.5 244.9l63.7-124.8c-57.6 10.8-113.2-20.8-139.5-72.5z"></path></svg>
                Let's Sync Your Posts
              </Button>
            )}
          </div>
        ) : (
          <ReelGrid
            scrollContainerRef={mainScrollRef}
            reels={reels}
            loading={loading}
            initialLoading={initialLoading}
            isBlurred={isSampleData}
            onLoadMore={handleLoadMore}
            hasMore={hasMore}
            viewMode={viewMode}
            onNoteUpdate={handleNoteUpdate}
            onCategoryUpdate={handleCategoryUpdate}
            onPostDelete={handlePostDelete}
            onPostArchive={handlePostArchive}
            favouriteStates={favouriteStates}
            onFavouriteToggle={handleFavouriteToggle}
            categories={allCategories}
            selectionMode={selectionMode}
            selectedPostIds={selectedPostIds}
            onToggleSelect={handleToggleSelectPost}
          />
        )}
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {showPhoneModal && (
        <PhoneNumberModal
          onClose={() => {
            setShowPhoneModal(false);
            localStorage.setItem('phoneModalLastShown', new Date().toDateString());
          }}
        />
      )}

      {/* Unprocessed posts – export dialog (Pro/Export users) */}
      {showUnprocessedExportDialog && (() => {
        const processingInProgress = syncStatus?.sync_status === 'PENDING' || syncStatus?.sync_status === 'PROCESSING' || syncStatus?.sync_status === 'PARTIALLY_PROCESSED';
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Unprocessed posts detected</h3>
                <button
                  onClick={() => setShowUnprocessedExportDialog(false)}
                  className="text-gray-400 bg-transparent hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              {processingInProgress ? (
                <>
                  <p className="text-gray-300 mb-6">
                    There are still {unprocessedCount} post{unprocessedCount === 1 ? '' : 's'} under processing. If you continue to export now, these won&apos;t be included in the report.
                  </p>
                  <div className="flex space-x-3">
                    <Button
                      onClick={() => {
                        setShowUnprocessedExportDialog(false);
                        handleExport();
                      }}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Export anyway
                    </Button>
                    <Button
                      onClick={() => setShowUnprocessedExportDialog(false)}
                      variant="outline"
                      className="flex-1 bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                    >
                      I will wait
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-gray-300 mb-6">
                    {unprocessedCount} post{unprocessedCount === 1 ? '' : 's'} haven&apos;t been processed yet.
                    If you continue to export now, they won&apos;t be included in this export.
                    <br />
                    <br />
                    <strong className="text-white font-semibold">Would you like to process them before exporting?</strong>
                  </p>
                  <div className="flex space-x-3">
                    <Button
                      onClick={() => {
                        setShowUnprocessedExportDialog(false);
                        handleExport();
                      }}
                      variant="outline"
                      className="flex-1 bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                    >
                      Export anyway
                    </Button>
                    <Button
                      onClick={handleUnprocessedProcessNow}
                      disabled={isProcessingForExport}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {isProcessingForExport ? (
                        <>
                          <Loader className="w-4 h-4 mr-2 inline animate-spin" />
                          Processing...
                        </>
                      ) : (
                        `Process ${unprocessedCount} post${unprocessedCount === 1 ? '' : 's'}`
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {showAiCategorisationDialog && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="relative bg-[#1a1f2e] border border-gray-700/80 rounded-xl p-6 sm:p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
            <button
              type="button"
              onClick={() => setShowAiCategorisationDialog(false)}
              className="absolute top-4 bg-transparent right-4 text-gray-400 hover:text-white z-10"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            <header className="text-center pr-8 mb-8 sm:mb-10">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
                Advanced AI Categorisation
              </h3>
              <p className="text-sm sm:text-base text-gray-400 leading-relaxed max-w-2xl mx-auto">
                We&apos;ll automatically organise your content as accurately as possible. Some posts may be harder to
                classify perfectly.
              </p>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 mb-8">
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 sm:items-start">
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-xs font-semibold uppercase tracking-wide text-violet-400 mb-2">Step 1</p>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    We first group your content into broader categories for easier organisation.
                  </p>
                </div>
                <div className="shrink-0 mx-auto sm:mx-0 sm:mt-0">
                  <img
                    src="/assets/ai_cat_ss_1.png"
                    alt="Broader categories list in the sidebar"
                    className="w-[200px] sm:w-[220px] border border-gray-600/80 shadow-md object-cover object-left-top"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 sm:items-start">
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-xs font-semibold uppercase tracking-wide text-violet-400 mb-2">Step 2</p>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    If a post doesn&apos;t clearly fit a category, we&apos;ll assign smart labels instead. You&apos;ll
                    be able to view both categories and labels in your sidebar.
                  </p>
                </div>
                <div className="shrink-0 mx-auto sm:mx-0 sm:mt-0">
                  <img
                    src="/assets/ai_cat_ss_2.png"
                    alt="Smart labels list in the sidebar"
                    className="w-[200px] sm:w-[220px] border border-gray-600/80 shadow-md object-cover object-left-top"
                  />
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-purple-500/10 border-purple-500/30 px-4 py-3 mb-6">
              <p className="text-sm text-gray-300 leading-relaxed">
                <span className="font-medium text-gray-200">Note:</span> AI categorisation may occasionally make
                mistakes. You can edit, rename, or move categories and labels anytime to customise them.
              </p>
            </div>

            <Button
              onClick={handleStartAiCategorisation}
              disabled={isStartingAiCategorisation}
              className="w-full rounded-lg bg-violet-600 hover:bg-violet-500 text-white font-medium py-2 h-auto disabled:opacity-60"
            >
              {isStartingAiCategorisation ? 'Starting...' : 'I understand, Continue'}
            </Button>
          </div>
        </div>
      )}

      {showAiCategorisationUpgradeDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Advanced ai categorisation</h3>
              <button
                onClick={() => setShowAiCategorisationUpgradeDialog(false)}
                className="text-gray-400 bg-transparent hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex justify-center items-center mb-6">
              <div className="relative flex items-center justify-center">
                <Lottie
                  animationData={attentionAnimation}
                  autoplay
                  style={{ width: 180, height: 180 }}
                />
              </div>
            </div>
            <p className="text-gray-300 mb-6">
              Advanced AI categorisation is not included in your current plan. Upgrade to a plan that supports this feature to categorise your posts automatically.
            </p>
            <div className="flex space-x-3">
              <Button
                onClick={() => setShowAiCategorisationUpgradeDialog(false)}
                variant="outline"
                className="flex-1 bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
              >
                Not Now
              </Button>
              <Button
                onClick={() => {
                  setShowAiCategorisationUpgradeDialog(false);
                  window.location.href = `${window.location.origin}/profile?active=subscription`;
                }}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                Upgrade Now
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Export Subscription Dialog */}
      {showExportDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Export Feature</h3>
              <button
                onClick={() => setShowExportDialog(false)}
                className="text-gray-400 bg-transparent hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex justify-center items-center mb-6">
              <div className="relative flex items-center justify-center">
                {/* Lottie animation for attention */}
                <Lottie
                  animationData={attentionAnimation}
                  autoplay
                  style={{ width: 180, height: 180 }}
                />
              </div>
            </div>
            <p className="text-gray-300 mb-6">
              The Export feature is only available on Pro(Annual) and Export pass plans. Upgrade now to access it.
            </p>
            <div className="flex space-x-3">
              <Button
                onClick={() => setShowExportDialog(false)}
                variant="outline"
                className="flex-1 bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
              >
                Not Now
              </Button>
              <Button
                onClick={() => {
                  setShowExportDialog(false);
                  window.location.href = `${window.location.origin}/profile?active=subscription`;
                }}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                Upgrade Now
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Export Success Dialog */}
      {showExportSuccessDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md mx-4">

            {/* Success Animation */}
            <div className="flex justify-center items-center mb-6">
              <div className="relative flex items-center justify-center">
                {/* Lottie animation for success */}
                <Lottie
                  animationData={successAnimation}
                  loop={true}
                  autoplay
                  style={{ width: 120, height: 120 }}
                />
              </div>
            </div>

            <p className="text-gray-300 mb-6 text-center mt-12">
              Export request received, you will receive the exported posts in a sheet on your email.
            </p>
            <Button
              onClick={() => setShowExportSuccessDialog(false)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              OK
            </Button>
          </div>
        </div>
      )}

      {/* Export Error Dialog - UI driven by error.detail.code */}
      {showExportErrorDialog && (() => {
        const code = exportErrorCode;
        const config = code && code in EXPORT_ERROR
          ? EXPORT_ERROR[code as keyof typeof EXPORT_ERROR]
          : EXPORT_ERROR_DEFAULT;
        const message = exportErrorMessage ?? config.message;
        const closeDialog = () => {
          setShowExportErrorDialog(false);
          setExportErrorCode(null);
          setExportErrorMessage(null);
        };
        const handlePrimary = () => {
          closeDialog();
          if (config.primaryAction === 'subscription') {
            navigate('/profile?active=subscription');
          } else if (config.primaryAction === 'retry') {
            handleExport();
          }
        };
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Export Failed</h3>
                <button
                  onClick={closeDialog}
                  className="text-gray-400 bg-transparent hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex justify-center mb-6">
                <div className="relative w-20 h-20">
                  <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                    <svg
                      className="w-10 h-10 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </div>
                  <div className="absolute inset-0 w-20 h-20 bg-red-400 rounded-full animate-ping opacity-20" />
                </div>
              </div>

              <p className="text-gray-300 mb-6 text-center">{message}</p>
              <div className="flex space-x-3">
                <Button
                  onClick={closeDialog}
                  variant="outline"
                  className="flex-1 bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                >
                  {config.secondaryLabel}
                </Button>
                <Button
                  onClick={handlePrimary}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {config.primaryLabel}
                </Button>
              </div>
            </div>
          </div>
        );
      })()}
      <motion.div
        id="dashboard"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 1 }}
        className="h-screen flex flex-col bg-gray-900 overflow-hidden"
      >
        {/* Top Header Bar — collapses out of the way on scroll down (see isHeaderHidden) */}
        <div
          className={`bg-gray-800 overflow-hidden transition-all duration-300 ease-out ${
            isHeaderHidden
              ? 'max-h-0 border-b-0 py-0 opacity-0'
              : 'max-h-32 border-b border-gray-700 px-6 py-4 opacity-100'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-white">Instavault</h1>
              <span data-slot="badge" className="hidden md:inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&amp;>svg]:size-3 gap-1 [&amp;>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&amp;]:hover:bg-secondary/90 bg-purple-500/20 text-purple-300 border-purple-500/30">Dashboard</span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="relative flex-1">
                <SearchBar onSearch={handleSearch} disabled={loading} />
              </div>
              <button
                className="text-gray-400 hover:text-white relative"
                onClick={() => setIsFilterPopupOpen(true)}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
                </svg>
                {getAppliedFiltersCount() > 0 && (
                  <span className="absolute -top-1 -right-1 bg-purple-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {getAppliedFiltersCount()}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* MCP Feature Bar */}
        {showMcpBanner && (
          <div className="border-b border-blue-500/40 px-4 py-2 flex items-center justify-center relative" style={{ backgroundColor: '#182652' }}>
            <div className="flex items-center gap-2 text-sm">
              <NewSidebarBadge />
              <span className="text-gray-300">Connect your saves to Claude, ChatGPT &amp; more via Instavault MCP</span>
              <p
                onClick={() => navigate('/profile?active=mcp')}
                className="text-blue-400 bg-transparent hover:text-blue-300 font-medium transition-colors cursor-pointer"
              >
                Setup Now
              </p>
            </div>
            <p
              onClick={() => {
                setShowMcpBanner(false);
                localStorage.setItem('mcpBannerDismissed', 'true');
              }}
              className="absolute clickable-text right-4 bg-transparent text-gray-500 hover:text-gray-300 transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </p>
          </div>
        )}

        <div className="flex-1 min-h-0 flex flex-col md:flex-row relative">
          {/* Mobile Header */}
          <div className="md:hidden flex justify-between items-center mb-4 p-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setIsMenuOpen(!isMenuOpen);
              }}
              className="text-white bg-transparent border-gray-400"
            >
              <Menu className="h-5 w-5" />
            </Button>

            {/* Mobile Action Buttons - Aligned with menu button */}
            <div className="flex items-center gap-2">
              {/* Bulk selection toggle — hidden when the user has no real posts here
                  (sample/placeholder data doesn't count) */}
              {reels.length > 0 && !isSampleData && (
                <Button
                  variant="outline"
                  size="icon"
                  className={`text-white hover:bg-gray-600 w-auto px-2 ${selectionMode ? 'bg-purple-600 hover:bg-purple-700 border-purple-600' : 'bg-gray-700 border-gray-600'}`}
                  disabled={loading}
                  onClick={handleToggleSelectionMode}
                  title={selectionMode ? 'Cancel Selection' : 'Select posts'}
                >
                  <CheckSquare className="h-4 w-4" />
                </Button>
              )}

              {/* Export Dropdown - only show when there are posts */}
              {reels.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className={`flex items-center gap-2 bg-gray-700 border-gray-600 text-white hover:bg-gray-600 w-auto ${isExporting ? 'animate-pulse' : ''}`}
                      disabled={loading || isExporting}
                      title={isExporting ? 'Exporting...' : 'Export'}
                    >
                      {isExporting ? (
                        <svg
                          className="w-4 h-4 animate-spin"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                      ) : (
                        <Upload />
                      )}
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-gray-700 border-gray-600">
                    <DropdownMenuItem
                      onClick={handleExportClick}
                      disabled={loading || isExporting}
                      className="text-white hover:bg-gray-600 cursor-pointer"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Export to Sheets
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate('/profile?active=integrations')}
                      className="text-white hover:bg-gray-600 cursor-pointer"
                    >
                      <Database className="h-4 w-4 mr-2" />
                      Export to Notion
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="flex items-center gap-2 bg-gray-700 border-gray-600 text-white hover:bg-gray-600 w-auto"
                    disabled={loading}
                  >
                    <ArrowUpDown className="h-4 w-4" />
                    <span className="text-xs">
                      {sortOrder === 'desc' ? 'Newest First' : 'Oldest First'}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-gray-700 border-gray-600">
                  <DropdownMenuRadioGroup value={sortOrder} onValueChange={(value) => {
                    const newSortOrder = value as 'asc' | 'desc';
                    trackSortOrderChanged(newSortOrder);
                    setSortOrder(newSortOrder);
                  }}>
                    <DropdownMenuRadioItem value="desc" className="text-white hover:bg-gray-600">
                      Newest First
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="asc" className="text-white hover:bg-gray-600">
                      Oldest First
                    </DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>

              <div className="flex bg-gray-700 rounded-lg p-1 w-auto">
                <button
                  className={`flex-1 p-2 rounded transition-colors ${viewMode === 'grid' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'}`}
                  onClick={() => setViewMode('grid')}
                  title="Grid View"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  className={`flex-1 p-2 rounded transition-colors ${viewMode === 'list' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'}`}
                  onClick={() => setViewMode('list')}
                  title="List View"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Unified Sidebar - Hidden on mobile unless menu is open */}
          {(!syncStatus || syncStatus.sync_status === 'SUCCESS' || syncStatus.sync_status === 'PARTIALLY_PROCESSED') && (
            <div className={`${isMenuOpen ? 'block' : 'hidden'} md:block absolute md:relative top-0 left-0 w-64 md:w-auto h-full md:h-full z-50 md:z-auto bg-gray-900 md:bg-transparent md:overflow-y-auto`}>
              {/* Close button for mobile - positioned within sidebar */}
              {isMenuOpen && (
                <div className="md:hidden absolute top-0 left-0 right-0 z-10 bg-slate-900/95 backdrop-blur-sm">
                  <div className="flex justify-end pt-4 pr-4">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setIsMenuOpen(false);
                      }}
                      className="text-white hover:bg-gray-700 bg-transparent border-gray-400"
                    >
                      <X className="h-6 w-6" />
                    </Button>
                  </div>
                </div>
              )}
              <Sidebar
                isMenuOpen={isMenuOpen}
                selectedCategory={selectedCategory}
                setSelectedCategory={(category) => {
                  // Reset archived, favorites, and all view when selecting any category
                  if (viewingArchived) {
                    setViewingArchived(false);
                  }
                  if (viewingFavorites) {
                    setViewingFavorites(false);
                  }
                  if (category) {
                    // Find category by ID and get its enum
                    trackCategoryFilterApplied(category.category_enum, category.category_display_value);
                  }

                  setSelectedCategory(category);
                  // Only close menu if it's a user click, not initialization
                  if (isMenuOpen && !isInitializing.current) {
                    setIsMenuOpen(false);
                  }
                  // Scroll to top when selecting a category
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                selectedPlatform={selectedPlatform}
                setSelectedPlatform={(platform) => {
                  // Track filter application
                  if (platform !== 'all_platforms') {
                    trackPlatformFilterApplied(platform);
                  }

                  setSelectedPlatform(platform);
                  // Reset archived, favorites, and all view when selecting any platform
                  if (viewingArchived) {
                    setViewingArchived(false);
                  }
                  if (viewingFavorites) {
                    setViewingFavorites(false);
                  }
                  // Only close menu if it's a user click, not initialization
                  if (isMenuOpen && !isInitializing.current) {
                    setIsMenuOpen(false);
                  }
                  // Scroll to top when selecting a platform
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="mb-8 md:mb-0"
                disabled={loading}
                viewingArchived={viewingArchived}
                onViewArchived={handleViewArchived}
                viewingFavorites={viewingFavorites}
                onViewFavorites={handleViewFavorites}
                categories={categories}
                favoriteCount={postCounts.favorite_count}
                archivedCount={postCounts.archived_count}
                platformCounts={platformCounts}
                totalPostsCount={totalPostsCount}
              />
            </div>
          )}

          {/* Main Content */}
          <div
            ref={mainScrollRef}
            className="flex-1 min-h-0 bg-slate-900/30 p-6 pt-0 sm:pt-6 overflow-y-scroll"
            onClick={() => {
              if (isMenuOpen) {
                setIsMenuOpen(false);
              }
            }}
          >
            {renderMainContent()}
            <SubscriptionModal />
          </div>
        </div>
      </motion.div>
      <Footer />
      <FilterPopup
        isOpen={isFilterPopupOpen}
        onClose={handleCloseFilterPopup}
        onApplyFilters={handleApplyFilters}
        appliedFilters={appliedFilters}
        selectedCategory={selectedCategory}
        categories={categories}
      />
      {/* Bulk Category Assignment Popup - adapted CategoryChangePopup in bulk mode (postIds instead of post) */}
      {isBulkCategoryPopupOpen && (
        <CategoryChangePopup
          postIds={Array.from(selectedPostIds)}
          isOpen={isBulkCategoryPopupOpen}
          onClose={() => setIsBulkCategoryPopupOpen(false)}
          onBulkCategoryUpdate={handleBulkCategoryUpdate}
          categories={allCategories}
        />
      )}
    </div>
  );
}
